import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { ArrowForward, FilterAlt } from "@mui/icons-material";
import {
  Box,
  Button,
  Portal,
  SxProps,
  Theme,
  Typography,
  Switch as UISwitch,
} from "@mui/material";
import { CenterPage } from "Components/CenterPage/CenterPage";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import GlassCard from "Components/GlassCard/GlassCard";
import Loading from "Components/Loading/Loading";
import { StyledFlexBox } from "Components/UI/StylesFlexBox";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import { useIsMobile } from "Hooks/useIsMobile";
import { useLeadStatusRules } from "Hooks/useLeadStatusRules";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useSelectedProject } from "Hooks";
import { TChartData } from "Interfaces";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { PageSubAction } from "consts";

export type SalesFlowFunnelEmptyVariant = "compact" | "page";

interface ISalesFlowFunnelChartProps {
  attributes?: ApiChartAttribute[];
  ignoreAttrModel?: boolean;
  noTitle?: boolean;
  /** Overrides mobile default for funnelAlwaysShowAdditionalMetrics */
  alwaysShowAdditionalMetrics?: boolean;
  emptyVariant?: SalesFlowFunnelEmptyVariant;
  /** Wrapper around the ready chart (e.g. CenterPage on the analysis page) */
  wrapReady?: (chart: ReactNode) => ReactNode;
  sx?: SxProps<Theme>;
  loadingText?: string;
  loadingFullHeight?: boolean;
  loadingSize?: number;
}

function ClosedFunnelToggle({
  closedFunnel,
  onChange,
}: {
  closedFunnel: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <StyledFlexBox compact>
      <Box display="flex" alignItems="center" mr={1}>
        <UISwitch
          checked={closedFunnel}
          onChange={(_e, checked) => onChange(checked)}
        />
        <Typography color={closedFunnel ? "primary" : "secondary"}>
          Closed Funnel
        </Typography>
      </Box>
      <ClickableTooltip
        placement="right"
        text={
          <>
            <p>
              Open - Stats reflect leads that were in one of the stages during
              the selected timeframe, even if they entered the funnel before
              that timeframe.
            </p>
            <p>
              Closed - Stats only reflect leads whose funnel start date is
              within the selected timeframe. This gives a clearer view of
              progression through stages for leads that started in-period.
            </p>
          </>
        }
        icon={<StyledHelp />}
      />
    </StyledFlexBox>
  );
}

function SalesFlowFunnelChart({
  attributes = [],
  ignoreAttrModel,
  noTitle,
  alwaysShowAdditionalMetrics,
  emptyVariant = "compact",
  wrapReady,
  sx,
  loadingText,
  loadingFullHeight,
  loadingSize,
}: ISalesFlowFunnelChartProps) {
  const isMobile = useIsMobile();
  const { leadStatusRules, fetched } = useLeadStatusRules();
  const { pathByName } = useProtectedRoutes();
  const { selectedProject } = useSelectedProject();
  const history = useHistory();
  const [closedFunnel, setClosedFunnel] = useState(false);

  const showAdditionalMetrics = alwaysShowAdditionalMetrics ?? isMobile;
  const isPage = emptyVariant === "page";

  const stageNames = useMemo(
    () =>
      [...leadStatusRules]
        .sort((a, b) => {
          const orderA =
            a.stageOrder !== undefined
              ? Number(a.stageOrder)
              : Number.MAX_SAFE_INTEGER;
          const orderB =
            b.stageOrder !== undefined
              ? Number(b.stageOrder)
              : Number.MAX_SAFE_INTEGER;
          if (orderA !== orderB) return orderA - orderB;
          return Number(a.id || 0) - Number(b.id || 0);
        })
        .map((rule) => rule.name || "")
        .filter((name) => name !== ""),
    [leadStatusRules],
  );

  const customDataFn = useCallback(
    (rows: TChartData[]) => {
      const normalized = new Map(
        rows.map((row) => [String(row.name || "").toLowerCase(), row]),
      );
      return stageNames
        .map((stage) => normalized.get(stage.toLowerCase()))
        .filter(Boolean) as TChartData[];
    },
    [stageNames],
  );

  const chart = useMemo(
    () => ({
      name: "Sales Flow Funnel",
      dimension: [ApiDimension.DIMENSION_FUNNEL_STAGE],
      template: ApiChartTemplate.SNAPSHOT,
      type: ApiChartType.FUNNEL,
      comparison: false,
      dateDimension: closedFunnel
        ? ApiDimension.DIMENSION_FUNNEL_START_DATE
        : undefined,
      attributes,
      yAxisKeys: [
        {
          key: "l",
          fields: [
            ApiMetric.METRIC_FUNNEL_STAGE_COUNT,
            ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
            ApiMetric.METRIC_LEADS_COUNT,
            ApiMetric.METRIC_PAID_SESSION_COUNT,
            ApiMetric.METRIC_ORGANIC_SESSION_COUNT,
            ApiMetric.METRIC_PAGEVIEWS_COUNT,
          ],
        },
      ],
      displayOptions: {
        noAttributionToggle: true,
        funnelUseLogFn: true,
        noTitle,
        funnelAlwaysShowAdditionalMetrics: showAdditionalMetrics,
        funnelAdditonalMetrics: stageNames.map((stage) => ({
          dimensionKey: stage,
          metrics: [
            ApiMetric.METRIC_LEADS_COUNT,
            ApiMetric.METRIC_PAGEVIEWS_COUNT,
            ApiMetric.METRIC_PAID_SESSION_COUNT,
            ApiMetric.METRIC_ORGANIC_SESSION_COUNT,
            ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
          ],
        })),
      },
    }),
    [attributes, closedFunnel, noTitle, showAdditionalMetrics, stageNames],
  );

  if (!fetched) {
    if (isPage) {
      return (
        <Loading
          fullHeight={loadingFullHeight ?? true}
          size={loadingSize ?? 60}
          text={loadingText ?? "Fetching Funnel Data"}
        />
      );
    }
    return (
      <Box sx={{ height: 200, flexShrink: 0, width: "100%" }}>
        <Loading
          size={loadingSize ?? 40}
          text={loadingText ?? "Loading sales flow…"}
        />
      </Box>
    );
  }

  if (stageNames.length === 0) {
    if (isPage) {
      return (
        <CenterPage calcHeight sm={11}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <GlassCard
              boxProps={{
                px: { xs: 3, sm: 5 },
                py: 4,
                sx: { width: 800, maxWidth: "100%" },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(201, 211, 244, 0.25)",
                  }}
                >
                  <FilterAlt />
                </Box>

                <Typography variant="h4" fontWeight={600}>
                  You Haven't Set Up Your Funnel Yet
                </Typography>

                <Typography variant="body1" color="text.secondary">
                  Create your funnel stages and rules before using Funnel
                  Analysis.
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ArrowForward />}
                  onClick={() => {
                    if (!selectedProject?.id || !selectedProject.clientId) {
                      return;
                    }
                    history.push(
                      pathByName(RouteNames.LEAD_STATUSES, {
                        clientId: selectedProject.clientId,
                        projectId: selectedProject.id,
                      }),
                    );
                  }}
                >
                  Go To Funnel Stages Page
                </Button>
              </Box>
            </GlassCard>
          </Box>
        </CenterPage>
      );
    }

    return (
      <GlassCard
        sx={{ flexShrink: 0, width: "100%" }}
        boxProps={{ p: 3, textAlign: "center" }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
          Sales Flow Funnel
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set up funnel stages in Funnel Analysis to see your sales flow here.
        </Typography>
      </GlassCard>
    );
  }

  const closedFunnelToggle = (
    <ClosedFunnelToggle
      closedFunnel={closedFunnel}
      onChange={setClosedFunnel}
    />
  );

  const chartNode = (
    <>
      {isPage && (
        <Portal container={() => document.getElementById(PageSubAction)}>
          {closedFunnelToggle}
        </Portal>
      )}
      <Box
        sx={[
          {
            height: isPage ? 600 : { xs: 360, md: 420 },
            flexShrink: 0,
            width: "100%",
            minWidth: 0,
            overflow: "auto",
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <AnalyticsCard
          noToolbar
          ignoreAttrModel={ignoreAttrModel}
          customDataFn={customDataFn}
          funnelShowStageChange
          customRightTop={!isPage ? closedFunnelToggle : undefined}
          chart={chart}
        />
      </Box>
    </>
  );

  return wrapReady ? <>{wrapReady(chartNode)}</> : chartNode;
}

export default SalesFlowFunnelChart;
